import Modal from '@/common/components/Modal';
import { ConsumptionData } from '../types/consumption.type';
import { useState } from 'react';
import Checkbox from '@/common/components/Checkbox';
import { PrinterIcon } from 'lucide-react';
import { useTransactionIdentifierWithReceipt } from '@/common/hooks/queries/useTransactionIdentifierWithReceipt';
import usePrint from '@/common/hooks/usePrint';
import PrintContext from '@/common/components/PrintContext';
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple';
import useUserPermissions from '@/common/hooks/useUserPermissions';

interface ModalSuccessProps {
  data: ConsumptionData;
  setData: React.Dispatch<React.SetStateAction<boolean>>;
  clearForm: () => void;
}

const ModalSuccess = ({ data, setData, clearForm }: ModalSuccessProps) => {
  const { hasUserPermission } = useUserPermissions();

  const hasGetTransactionIdentifierPermission = hasUserPermission('Admin::Billing::TransactionIdentifiers::ShowWithReceipt');

  const [doNotPrintReceipt, setDoNotPrintReceipt] = useState(false)

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { print, contextProps } = usePrint({
    onAfterPrint: () => {
      setData(false);
      clearForm();
    },
  });

  const {
    mutate: getTransactionIdentifier,
    isPending: isGetTransactionIdentifierPending,
  } = useTransactionIdentifierWithReceipt({
    transactionIdentifierId: data?.protocol_number,
    onSuccess: (data) => {
      const receipt = data?.eventable?.receipt?.text;

      if (receipt) {
        print({ content: receipt });
      } else {
        setMessages(['Ocorreu um erro ao buscar as informações do comprovante'])
      }
    },
    onError: (error) => {
      setMessages(error.response?.data.errors.base || ['Ocorreu um erro ao buscar as informações do comprovante'])
    },
  })

  const closeModalAndClearForm = () => {
    if (!hasGetTransactionIdentifierPermission || doNotPrintReceipt) {
      setData(false);
      clearForm();
    } else {
      getTransactionIdentifier();
    }
    setDoNotPrintReceipt(false);
  };

  return (
    <>
      <Modal
        isOpen={!!data}
        onConfirm={closeModalAndClearForm}
        Trigger={<></>}
        showClose={false}
        dataTestid="confirm_modal_button"
        footerIcon={
          (hasGetTransactionIdentifierPermission && !doNotPrintReceipt)
            ? <PrinterIcon strokeWidth={1.5} size={16} className='-mr-2' />
            : undefined
        }
        footer={
          hasGetTransactionIdentifierPermission ?
            !doNotPrintReceipt
              ? 'Imprimir comprovante'
              : 'Finalizar transação sem comprovante'
            : 'Entendido!'
        }
        confirmButtonIsLoading={isGetTransactionIdentifierPending}
        disabled={isGetTransactionIdentifierPending}
        title="Transação bem sucedida!"
        body={(
          <div className="flex flex-col gap-8 py-2">
            <div className="flex flex-col gap-2">
              <div>
                <p className="text-[14px] font-light">ID da requisição</p>
                <p className="text-[16px] text-2xl text-muted">{data.id}</p>
              </div>
              <div>
                <p className="text-[14px] font-light">Número de protocolo</p>
                <p className="text-[16px] text-2xl text-muted">{data.protocol_number}</p>
              </div>
            </div>

            {hasGetTransactionIdentifierPermission && (
              <div>
                <Checkbox
                  name="do_not_print_receipt"
                  checked={doNotPrintReceipt}
                  onChange={(e) => setDoNotPrintReceipt(e.currentTarget.checked)}
                  label='Não desejo imprimir compovante'
                  labelClassName='italic text-white-800 select-none'
                />
              </div>
            )}
          </div>
        )}
      />

      <PrintContext {...contextProps} className="text-black-900 px-1" />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
          onClose={() => {
            setData(false);
            clearForm();
          }}
        />
      )}
    </>
  );
};

export default ModalSuccess;
